import { EconomicEventOutput } from '@api/api/financial-events/economics-events';
import { FlagIcon } from '@design/components/flag-icon/FlagIcon';
import {
  type AppTableFeatures,
  WithNAFallback,
} from '@design/components/table';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { getPriceStatus, PriceStatus } from '@helpers/utils/numbers/numbers';
import { ColumnDef } from '@tanstack/react-table';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import { format } from 'date-fns';
import { t } from 'i18next';
import tableStyles from '../EventsTable.module.scss';
import { SORT_UNDEFINED } from './constants';

const priceStatusClassMap: Record<PriceStatus, string> = {
  [PriceStatus.Default]: semanticColorStyles.directionNeutralDefault,
  [PriceStatus.Negative]: semanticColorStyles.directionNegative,
  [PriceStatus.Positive]: semanticColorStyles.directionPositive,
};

export const getEconomicsColumns = (): ColumnDef<
  AppTableFeatures,
  EconomicEventOutput
>[] => [
  {
    id: 'date',
    header: t('calendar:table.time'),
    cell: props => {
      const { date } = props.row.original;

      return (
        <WithNAFallback
          check={!!date}
          value={<span>{format(new Date(date), 'HH:mm')}</span>}
        />
      );
    },
    accessorFn: row => new Date(row.date),
    sortFn: 'datetime',
  },
  {
    id: 'name',
    header: t('calendar:table.eventName'),
    cell: props => {
      const { name, country } = props.row.original;

      return (
        <WithNAFallback
          check={!!name && !!country}
          value={
            <div className={tableStyles.eventNameWrapper}>
              <FlagIcon
                countryCode={country}
                variant="circular"
                customSize={20}
              />
              <span className={tableStyles.nortFontText}>{name}</span>
            </div>
          }
        />
      );
    },
    accessorFn: row => row.name,
    sortUndefined: SORT_UNDEFINED,
  },
  {
    id: 'category',
    header: t('calendar:table.eventCategory'),
    cell: props => {
      const { category } = props.row.original;

      return (
        <WithNAFallback
          check={!!category}
          value={<span className={tableStyles.nortFontText}>{category}</span>}
        />
      );
    },
    accessorFn: row => row.category,
    sortUndefined: SORT_UNDEFINED,
  },
  {
    id: 'period',
    header: t('calendar:table.refPeriod'),
    cell: props => {
      const { period } = props.row.original;

      return (
        <WithNAFallback
          check={!!period}
          value={<span className={tableStyles.nortFontText}>{period}</span>}
        />
      );
    },
    accessorFn: row => row.period,
    sortUndefined: SORT_UNDEFINED,
  },
  {
    id: 'consensus',
    header: t('calendar:table.expected'),
    cell: props => {
      const { consensus } = props.row.original;

      return (
        <WithNAFallback
          value={
            consensus !== null &&
            consensus !== undefined && (
              <span>
                {formatNumber({ isPercentage: false, value: consensus })}
              </span>
            )
          }
        />
      );
    },
    accessorFn: row => (row.consensus === null ? undefined : row.consensus),
    sortUndefined: SORT_UNDEFINED,
  },
  {
    id: 'reported',
    header: t('calendar:table.reported'),
    cell: props => {
      const { actual_unit: actualUnit, actual } = props.row.original;
      const isPercentage = actualUnit === '%';

      return (
        <WithNAFallback
          value={
            actual !== undefined &&
            actual !== null && (
              <span>
                {formatNumber({
                  isPercentage,
                  value: actual,
                  addPlus: isPercentage,
                })}
              </span>
            )
          }
        />
      );
    },
    accessorFn: row => row.actual,
    sortUndefined: SORT_UNDEFINED,
  },
  {
    id: 'surprise',
    header: t('calendar:table.surprise'),
    cell: props => {
      const {
        surprise_unit: surpriseUnit,
        actual_unit: actualUnit,
        surprise,
      } = props.row.original;

      const isPercentage = (surpriseUnit ?? actualUnit) === '%';

      return (
        <WithNAFallback
          value={
            surprise !== undefined &&
            surprise !== null && (
              <div className={priceStatusClassMap[getPriceStatus(surprise)]}>
                {formatNumber({
                  isPercentage,
                  value: surprise,
                  addPlus: isPercentage,
                })}
              </div>
            )
          }
        />
      );
    },
    accessorFn: row => row.surprise,
    sortUndefined: SORT_UNDEFINED,
  },
];
